import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartHandshake, Users, Building2, Briefcase, Home,
  ArrowLeft, ArrowRight, ShieldAlert, FileSearch, UserCheck,
  Calculator, Settings2, MonitorSmartphone, Sparkles, MessageCircle
} from 'lucide-react';

const PROFILI = [
  { id: 'coop', label: 'Gestisco una Cooperativa', icon: HeartHandshake },
  { id: 'ets', label: "Rappresento un'Associazione, un ETS, APS, ODV, ecc.", icon: Users },
  { id: 'altri', label: "Altri: gestisco un'impresa, sono un professionista", icon: Briefcase },
  { id: 'famiglia', label: 'Cerco protezione per me o la mia famiglia', icon: Home },
];

const QUALIFICAZIONI = {
  coop: {
    domanda: 'La tua cooperativa è associata a Confcooperative?',
    opzioni: [
      { id: 'confcoop', label: 'Associata a Confcooperative', icon: HeartHandshake },
      { id: 'altre', label: 'Altre / Non associata', icon: Building2 },
    ],
  },
  ets: {
    domanda: "L'ente è iscritto al RUNTS (Registro Unico Nazionale del Terzo Settore)?",
    opzioni: [
      { id: 'runts', label: 'Iscritta al RUNTS', icon: Users },
      { id: 'no-runts', label: 'Non iscritta al RUNTS', icon: Users },
    ],
  },
};

const NOTE_QUALIFICA = {
  confcoop: 'Come cooperativa associata a Confcooperative puoi accedere anche alle convenzioni e agli accordi dedicati del sistema cooperativo.',
  altre: null,
  runts: "L'iscrizione al RUNTS comporta anche obblighi specifici, come l'assicurazione dei volontari prevista dall'art. 18 del Codice del Terzo Settore.",
  'no-runts': "Possiamo aiutarti anche a valutare opportunità e requisiti dell'iscrizione al RUNTS.",
};

const ESIGENZE = {
  org: [
    { id: 'rischi', label: 'Conoscere i rischi della mia organizzazione', icon: ShieldAlert },
    { id: 'coperture', label: 'Verificare le mie coperture assicurative', icon: FileSearch },
    { id: 'persone', label: 'Proteggere amministratori, dipendenti o volontari', icon: UserCheck },
    { id: 'preventivo', label: 'Richiedere un preventivo', icon: Calculator },
    { id: 'gestione', label: 'Migliorare la gestione della mia organizzazione', icon: Settings2 },
    { id: 'digitale', label: 'Conoscere gli strumenti digitali Assietica', icon: MonitorSmartphone },
  ],
  persona: [
    { id: 'rischi', label: 'Capire da quali rischi proteggermi', icon: ShieldAlert },
    { id: 'coperture', label: 'Verificare le mie coperture assicurative', icon: FileSearch },
    { id: 'rca', label: "Richiedere un preventivo RCA per il mio veicolo", icon: Calculator },
    { id: 'consulenza', label: 'Parlare con un consulente', icon: MessageCircle },
  ],
};

const profiloNome = {
  coop: 'cooperativa',
  ets: 'associazione o ETS',
  altri: 'impresa o attività professionale',
  famiglia: 'famiglia',
};

const RISULTATI = {
  rischi: (p) => ({
    titolo: 'Partiamo dalla conoscenza dei rischi',
    testo: p === 'famiglia'
      ? 'Ogni situazione personale e professionale ha esposizioni diverse: responsabilità verso terzi, salute, patrimonio, veicoli. Il primo passo è una fotografia chiara della tua situazione, senza impegno.'
      : `Una ${profiloNome[p]} è esposta a responsabilità verso terzi, lavoratori, volontari e amministratori, oltre ai rischi su immobili, automezzi e continuità operativa. Con Assietica Risk Experience puoi mapparle subito: un percorso guidato, anonimo e gratuito di circa 7 minuti, con report finale.`,
    cta: p === 'famiglia'
      ? [
          { label: 'Richiedi un\'analisi gratuita', to: '/#contatti' },
          { label: 'Compila la scheda di quotazione', to: '/quotazione' },
        ]
      : [
          { label: 'Inizia la Risk Experience', to: '/risk-experience' },
          { label: 'Richiedi un\'analisi gratuita', to: '/#contatti' },
        ],
  }),
  coperture: (p) => ({
    titolo: 'Verifica delle coperture esistenti',
    testo: 'Polizze stratificate nel tempo possono generare doppie coperture o lasciare scoperte aree importanti. Un check-up con un consulente ti permette di capire cosa è coperto, cosa no e cosa è cambiato rispetto a quando hai sottoscritto i contratti.',
    cta: [
      { label: 'Prenota il check-up gratuito', to: '/#contatti' },
      { label: 'Leggi la documentazione precontrattuale', to: '/precontrattuale' },
    ],
  }),
  persone: () => ({
    titolo: 'Proteggere chi fa vivere la tua organizzazione',
    testo: "Amministratori (responsabilità gestionali), dipendenti (infortuni e tutele), volontari (obblighi assicurativi previsti dal Codice del Terzo Settore) e tirocinanti: ogni figura ha esigenze di protezione specifiche. Per i tirocini abbiamo un percorso dedicato già attivo.",
    cta: [
      { label: 'RC Tirocini / INCLUDIS', to: '/tirocini' },
      { label: 'Parla con un consulente', to: '/#contatti' },
    ],
  }),
  preventivo: () => ({
    titolo: 'Il preventivo giusto parte dalle informazioni giuste',
    testo: 'Puoi richiedere subito una quotazione per la tua organizzazione con la scheda guidata, oppure un preventivo RCA per i tuoi veicoli: con "Basta la Targa" servono pochi dati.',
    cta: [
      { label: 'Scheda di quotazione', to: '/quotazione' },
      { label: 'RCA — Basta la Targa', to: '/basta-la-targa' },
    ],
  }),
  gestione: () => ({
    titolo: 'Strumenti e accordi per gestire meglio',
    testo: "Oltre alla consulenza assicurativa, Assietica mette a disposizione strumenti digitali per la gestione degli ETS e una Rete di Accordi con condizioni di maggior favore per soci, dipendenti e volontari degli enti convenzionati.",
    cta: [
      { label: 'Scopri la Rete di Accordi', to: '/rete-di-accordi' },
      { label: 'Richiedi informazioni', to: '/#contatti' },
    ],
  }),
  digitale: () => ({
    titolo: "L'ecosistema digitale Assietica",
    testo: 'AssieticaGest per la gestione degli ETS, il Portale Partner per gli accordi dedicati e nuovi strumenti in arrivo: scopri come la tecnologia può semplificare la vita della tua organizzazione.',
    cta: [
      { label: 'Rete di Accordi', to: '/rete-di-accordi' },
      { label: 'Richiedi la brochure del Gestionale', to: '/#footer-brochure' },
    ],
  }),
  rca: () => ({
    titolo: 'Preventivo RCA in pochi minuti',
    testo: 'Con "Basta la Targa" bastano la targa del veicolo e pochi dati di contatto: penseremo noi a prepararti una proposta.',
    cta: [
      { label: 'Vai a Basta la Targa', to: '/basta-la-targa' },
    ],
  }),
  consulenza: () => ({
    titolo: 'Un consulente al tuo fianco',
    testo: 'Raccontaci la tua situazione: un consulente Assietica ti ricontatterà per un confronto gratuito e senza impegno.',
    cta: [
      { label: 'Prenota la consulenza', to: '/#contatti' },
    ],
  }),
};

const StepDots = ({ step, total }) => (
  <div className="flex items-center justify-center gap-2 mb-8" aria-hidden="true">
    {Array.from({ length: total }, (_, i) => i + 1).map((s) => (
      <span
        key={s}
        className={`h-2 rounded-full transition-all duration-300 ${s === step ? 'w-8 bg-[#8FE3DA]' : 'w-2 bg-white/40'}`}
      />
    ))}
  </div>
);

const ExperienceBlock = () => {
  const [step, setStep] = useState(1);
  const [profilo, setProfilo] = useState(null);
  const [qualifica, setQualifica] = useState(null);
  const [esigenza, setEsigenza] = useState(null);

  const isPersona = profilo === 'famiglia';
  const hasQualifica = profilo === 'coop' || profilo === 'ets';
  const totalSteps = hasQualifica ? 4 : 3;
  const esigenze = isPersona ? ESIGENZE.persona : ESIGENZE.org;
  const risultato = esigenza ? RISULTATI[esigenza](profilo) : null;
  const notaQualifica = qualifica ? NOTE_QUALIFICA[qualifica] : null;
  const profiloLabel = PROFILI.find((p) => p.id === profilo)?.label;
  const qualificaLabel = hasQualifica && qualifica
    ? QUALIFICAZIONI[profilo].opzioni.find((o) => o.id === qualifica)?.label
    : null;

  const stepEsigenza = hasQualifica ? 3 : 2;
  const stepRisultato = hasQualifica ? 4 : 3;

  const selectProfilo = (id) => {
    setProfilo(id);
    setQualifica(null);
    setEsigenza(null);
    setStep(2);
  };

  const reset = () => { setStep(1); setProfilo(null); setQualifica(null); setEsigenza(null); };

  const scrollOrNavigate = (to) => {
    if (to.startsWith('/#')) {
      const id = to.slice(2);
      const el = document.getElementById(id) || document.querySelector('footer');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return true;
    }
    return false;
  };

  return (
    <section id="experience" className="bg-gradient-to-br from-[#1B2380] via-[#0E8C84] to-[#0E8C84] py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-xs font-semibold px-3.5 py-1.5 rounded-full mb-4">
            <Sparkles size={13} className="text-[#8FE3DA]" /> Assietica Experience
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Come possiamo aiutarti oggi?
          </h2>
          <p className="text-white/85 mt-3 max-w-xl mx-auto text-base">
            Due domande per orientarti subito verso il percorso più utile. Nessuna registrazione richiesta.
          </p>
        </div>

        <StepDots step={step} total={totalSteps} />

        {/* STEP 1 — Profilo */}
        {step === 1 && (
          <div data-testid="experience-step-profilo" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROFILI.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  data-testid={`profilo-${p.id}`}
                  onClick={() => selectProfilo(p.id)}
                  className="group bg-white rounded-2xl p-5 text-left lg:text-center shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex lg:flex-col items-center gap-4 lg:gap-3 min-h-[72px]"
                >
                  <span className="w-12 h-12 rounded-xl bg-[#1B2380]/10 group-hover:bg-[#8FE3DA]/25 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                    <Icon size={24} className="text-[#1B2380]" />
                  </span>
                  <span className="text-sm font-semibold text-gray-800 leading-snug">{p.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* STEP 2 — Qualificazione (solo coop ed ETS) */}
        {step === 2 && hasQualifica && (
          <div data-testid="experience-step-qualifica">
            <p className="text-center text-white/90 text-sm mb-5">
              {QUALIFICAZIONI[profilo].domanda}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {QUALIFICAZIONI[profilo].opzioni.map((o) => {
                const Icon = o.icon;
                return (
                  <button
                    key={o.id}
                    data-testid={`qualifica-${o.id}`}
                    onClick={() => { setQualifica(o.id); setStep(3); }}
                    className="group bg-white rounded-2xl p-5 text-left shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-4"
                  >
                    <span className="w-11 h-11 rounded-xl bg-[#0E8C84]/10 group-hover:bg-[#8FE3DA]/25 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <Icon size={22} className="text-[#1B2380]" />
                    </span>
                    <span className="text-sm font-semibold text-gray-800 leading-snug">{o.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <button
                data-testid="experience-back-qualifica"
                onClick={() => { setStep(1); setQualifica(null); }}
                className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-300"
              >
                <ArrowLeft size={15} /> Torna indietro
              </button>
            </div>
          </div>
        )}

        {/* STEP — Esigenza */}
        {step === stepEsigenza && (!hasQualifica || qualifica) && (
          <div data-testid="experience-step-esigenza">
            <p className="text-center text-white/90 text-sm mb-5">
              <span className="font-semibold text-[#8FE3DA]">{profiloLabel}</span>
              {qualificaLabel && <span className="text-white/70"> · {qualificaLabel}</span>}
              {' '}— qual è la tua esigenza principale?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {esigenze.map((e) => {
                const Icon = e.icon;
                return (
                  <button
                    key={e.id}
                    data-testid={`esigenza-${e.id}`}
                    onClick={() => { setEsigenza(e.id); setStep(stepRisultato); }}
                    className="group bg-white rounded-2xl p-5 text-left shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-4"
                  >
                    <span className="w-11 h-11 rounded-xl bg-[#0E8C84]/10 group-hover:bg-[#8FE3DA]/25 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <Icon size={22} className="text-[#1B2380]" />
                    </span>
                    <span className="text-sm font-semibold text-gray-800 leading-snug">{e.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <button
                data-testid="experience-back-1"
                onClick={() => { setStep(hasQualifica ? 2 : 1); setEsigenza(null); }}
                className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-300"
              >
                <ArrowLeft size={15} /> Torna indietro
              </button>
            </div>
          </div>
        )}

        {/* STEP — Orientamento */}
        {step === stepRisultato && risultato && (
          <div data-testid="experience-risultato" className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-7 sm:p-9">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">{risultato.titolo}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">{risultato.testo}</p>
              {notaQualifica && (
                <p data-testid="experience-nota-qualifica" className="text-sm text-[#1B2380] bg-[#1B2380]/5 border border-[#1B2380]/10 rounded-lg px-4 py-3 mb-6 leading-relaxed">
                  {notaQualifica}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                {risultato.cta.map((c, i) => (
                  <Link
                    key={c.to}
                    to={c.to.startsWith('/#') ? '/' : c.to}
                    data-testid={`experience-cta-${i}`}
                    onClick={(e) => { if (scrollOrNavigate(c.to)) e.preventDefault(); }}
                    className={`flex-1 inline-flex items-center justify-center gap-2 font-semibold py-3 px-4 rounded-xl text-sm transition-colors duration-300 ${
                      i === 0
                        ? 'bg-[#1B2380] hover:bg-[#2A3299] text-white'
                        : 'border-2 border-[#1B2380] text-[#1B2380] hover:bg-[#1B2380]/5'
                    }`}
                  >
                    {c.label} <ArrowRight size={15} />
                  </Link>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-5 leading-relaxed">
                Orientamento informativo: non costituisce consulenza assicurativa né valutazione di adeguatezza.
              </p>
            </div>
            <div className="flex items-center justify-center gap-6 mt-6">
              <button
                data-testid="experience-back-2"
                onClick={() => { setStep(stepEsigenza); setEsigenza(null); }}
                className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-300"
              >
                <ArrowLeft size={15} /> Cambia esigenza
              </button>
              <button
                data-testid="experience-restart"
                onClick={reset}
                className="text-white/80 hover:text-white text-sm font-medium underline transition-colors duration-300"
              >
                Ricomincia
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExperienceBlock;
